import * as React from "react"

// Inline SavvyCal scheduler for the /book page.
//
// Replaces the previous Cal.com embed (removed 2026-08-27). That one pointed at
// a hardcoded cal.com link whose account slug 404'd, and CalEmbed rendered
// `null` when the embed failed — so three qualified applicants reached /book,
// saw an empty page, and left. Both halves of that are fixed here: the link is
// configured server-side, and a failure renders a visible booking button rather
// than nothing.
//
// Loaded from a <script> injected in useEffect so nothing evaluates during SSR.
// SavvyCal's API: SavvyCal('init') then SavvyCal('inline', {...}), and a
// `savvycal.scheduled` window event when someone books.
// https://docs.savvycal.com/article/6-embedding-links-on-your-website

const EMBED_SRC = "https://embed.savvycal.com/v1/embed.js"
const CONTAINER_ID = "savvycal-inline"

// If the script hasn't booted by now, treat it as blocked and show the
// fallback. Corporate networks (the exact audience for a Fit Call) block
// third-party embed hosts routinely, and a spinner that never resolves reads
// as a broken page.
const LOAD_TIMEOUT_MS = 8000

declare global {
  interface Window {
    SavvyCal?: ((...args: unknown[]) => void) & { q?: unknown[] }
  }
}

type Status = "loading" | "ready" | "failed"

export function SavvyCalEmbed({
  link,
  url,
  name,
  email,
  onBooked,
}: {
  /** SavvyCal path slug, e.g. "harryduran/viphd30" — not the full URL. */
  link: string
  /** Full public URL, used by the fallback and the "new tab" escape hatch. */
  url: string
  name: string
  email: string
  onBooked?: () => void
}) {
  const [status, setStatus] = React.useState<Status>("loading")

  React.useEffect(() => {
    let active = true
    const timer = setTimeout(() => active && setStatus("failed"), LOAD_TIMEOUT_MS)

    const booked = () => onBooked?.()
    window.addEventListener("savvycal.scheduled", booked)

    // The queue shim has to exist before embed.js loads, so calls made while
    // the script is still in flight are replayed once it boots.
    window.SavvyCal =
      window.SavvyCal ||
      function (...args: unknown[]) {
        ;(window.SavvyCal!.q = window.SavvyCal!.q || []).push(args)
      }

    const start = () => {
      if (!active) return
      try {
        window.SavvyCal!("init")
        window.SavvyCal!("inline", {
          link,
          selector: `#${CONTAINER_ID}`,
          email,
          displayName: name,
        })
        clearTimeout(timer)
        setStatus("ready")
      } catch {
        clearTimeout(timer)
        setStatus("failed")
      }
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${EMBED_SRC}"]`)
    if (existing) {
      start()
    } else {
      const script = document.createElement("script")
      script.src = EMBED_SRC
      script.async = true
      script.onload = start
      script.onerror = () => {
        if (!active) return
        clearTimeout(timer)
        setStatus("failed")
      }
      document.head.appendChild(script)
    }

    return () => {
      active = false
      clearTimeout(timer)
      window.removeEventListener("savvycal.scheduled", booked)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link, email, name])

  if (status === "failed") {
    return (
      <div className="rounded-xl border border-hairline bg-surface px-6 py-10 text-center">
        <p className="text-ink-display">The scheduler couldn't load on this network.</p>
        <p className="mt-2 text-ink-muted">
          It's usually a corporate firewall blocking the embed. The booking page
          itself works fine in a new tab.
        </p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onBooked}
          className="mt-6 inline-flex items-center rounded-lg bg-accent px-5 py-3 font-medium text-page no-underline"
        >
          Open the booking page
        </a>
      </div>
    )
  }

  return (
    <>
      {status === "loading" && (
        <div className="flex h-[600px] items-center justify-center rounded-xl border border-hairline bg-surface text-ink-muted">
          Loading the scheduler…
        </div>
      )}
      <div id={CONTAINER_ID} className={status === "ready" ? "min-h-[600px]" : "hidden"} />
    </>
  )
}
